import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

type ActionContext = {
  subscribe: (key: string, callback: (ev: any) => void) => () => void;
  emit: (key: string, data: any) => void;
  getLatestValue: (key: string) => any;
};

const noopActionContext: ActionContext = {
  subscribe: () => () => {},
  emit: () => {},
  getLatestValue: () => undefined,
};

const _ActionContext = createContext<ActionContext>(noopActionContext);

export function useEmit<T = void>(key: string) {
  const context = useContext(_ActionContext);
  return useCallback((data: T) => context.emit(key, data), [context]);
}

export function useSubscribe<T>(key: string, callback: (ev: T) => void) {
  const context = useContext(_ActionContext);

  useEffect(() => {
    return context.subscribe(key, callback);
  }, [key, callback, context]);
}

export function useLatestValue<T>(key: string) {
  const context = useContext(_ActionContext);
  const [value, setValue] = useState<T | undefined>(context.getLatestValue(key));
  useSubscribe(key, setValue);
  return value;
}

export function ActionsContext({ children }: { children?: ReactNode }) {
  const value = useMemo(() => new ObservableActions(), []);
  return <_ActionContext.Provider value={value}>{children}</_ActionContext.Provider>;
}

class ObservableActions implements ActionContext {
  private subscribers = new Map<string, Set<(ev: any) => void>>();
  private lastValue = new Map<string, any>();

  private subscribeOne(key: string, callback: (ev: any) => void) {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, new Set());
    }
    this.subscribers.get(key)!.add(callback);
    return () => {
      this.subscribers.get(key)!.delete(callback);
    };
  }

  emit(key: string, data: any) {
    this.lastValue.set(key, data);
    const subscribers = this.subscribers.get(key);
    if (subscribers) {
      for (const subscriber of subscribers) {
        subscriber(data);
      }
    }
  }

  subscribe(key: string, callback: (ev: any) => void) {
    const unsubscribes = [] as (() => void)[];
    key.split(" ").forEach((k) => unsubscribes.push(this.subscribeOne(k, callback)));
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }

  getLatestValue(key: string) {
    return this.lastValue.get(key);
  }
}
